import React, { Fragment } from "react";
import { createPortal } from "react-dom";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { Button } from "components/ui";
import { Input, Select, InputErrorMsg } from "components/ui/Form";
import { XMarkIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import CustomCreatableDropdown from "custom-components/shared/CustomCreatableDropdown";
import { EntityTypeEnum } from "enum/EntityTypeEnum";
import { EntityDynamicFieldsForm } from "components/shared/dynamic-fields";

import RichTextEditor from "components/shared/RichTextEditor/RichTextEditor";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
export default function AddEditNoteModal({
  dynamicFieldsRef,
  show,
  onClose,
  formValues,
  setFormValues,
  submitting,
  onSubmit,
  categories,
  addingCategory,
  handleCreateCategory,
  handleEditCategory,
  handleDeleteCategory,
  wishItems,
  addingWishItem,
  handleCreateWishItem,
  handleEditWishItem,
  handleDeleteWishItem,
  wishItemOptions,
  users = [],
  costAssignUsers = users,
  workStatuses = [],
  costStatuses = [],
  categoryOptions,
  noteTypeOptions = [],
  noteTypeLoading = false,
  onCreateNoteType,
  onEditNoteType,
  onDeleteNoteType,
  errors = {},
  setErrors,
  extraFieldsRequired = true,
  workStatusOptions = [],
  addingWorkStatus = false,
  handleCreateWorkStatus,
  handleEditWorkStatus,
  handleDeleteWorkStatus,
  costStatusOptions = [],
  addingCostStatus = false,
  handleCreateCostStatus,
  handleEditCostStatus,
  handleDeleteCostStatus,
  isStatusOptionActionable,
  onFieldAdded,
  entityTypeCode,
  isLead,
}) {

  return (
    <>
    <Transition appear show={show} as={Fragment}>
      <Dialog as="div" className="relative z-[200]" onClose={() => {}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative w-full max-h-[90vh] max-w-2xl transform overflow-hidden rounded-[20px] bg-white shadow-2xl transition-all dark:bg-gray-900 flex flex-col outline-none focus:outline-none">
                {/* Header */}
                <div className="flex-shrink-0 border-b border-gray-150 dark:border-gray-800 px-8 py-5 flex items-center justify-between">
                  <DialogTitle as="h3" className="text-[18px] font-bold text-gray-900 dark:text-white">
                    {formValues.id ? "Edit Client Note" : "Add Client Note"}
                  </DialogTitle>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => dynamicFieldsRef.current?.openAddFieldModal()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/10 rounded-lg transition-colors focus:outline-none cursor-pointer border border-primary-100 dark:border-primary-900/30"
                    >
                      <PlusCircleIcon className="size-4" />
                      Add Fields
                    </button>
                    <Button
                      onClick={onClose}
                      variant="flat"
                      isIcon
                      className="w-8 h-8 rounded-full text-gray-400 hover:text-gray-650 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                {/* Body Content */}
                <div className="flex-1 overflow-y-auto px-8 py-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                    {/* Note Type Dropdown (Spans 2 columns) */}
                    <div className="space-y-1.5 md:col-span-2">
                      <label className="block text-[11px] font-semibold  text-slate-500 dark:text-dark-300">
                        Note Type <span className="text-red-500">*</span>
                      </label>
                      <CustomCreatableDropdown
                        options={noteTypeOptions}
                        value={noteTypeOptions.find(opt => opt.value == formValues.noteTypeId) || null}
                        onChange={(newValue) => {
                          if (newValue) {
                            setFormValues(prev => ({ ...prev, noteTypeId: newValue.value }));
                          } else {
                            setFormValues(prev => ({ ...prev, noteTypeId: "" }));
                          }
                          if (errors.noteTypeId) {
                            setErrors(prev => ({ ...prev, noteTypeId: "" }));
                          }
                        }}
                        onCreateOption={onCreateNoteType}
                        onEditOption={onEditNoteType}
                        onDeleteOption={onDeleteNoteType}
                        placeholder="Search or type to add note type..."
                        isLoading={noteTypeLoading}
                        error={!!errors.noteTypeId}
                        menuHeader="Select Note Type"
                        addNewText="+ Add New Note Type"
                      />
                      <InputErrorMsg when={errors.noteTypeId}>{errors.noteTypeId}</InputErrorMsg>
                    </div>

                    {/* Category Dropdown (Spans 2 columns) */}
                    <div className="space-y-1.5 md:col-span-2">
                      <label className="block text-[11px] font-semibold  text-slate-500 dark:text-dark-300">
                        Category <span className="text-red-500">*</span>
                      </label>
                      <CustomCreatableDropdown
                        options={categoryOptions}
                        value={categoryOptions.find(opt => opt.value === formValues.categoryId?.toString()) || null}
                        onChange={(newValue) => {
                          if (newValue) {
                            setFormValues(prev => ({ ...prev, categoryId: newValue.value }));
                          } else {
                            setFormValues(prev => ({ ...prev, categoryId: "" }));
                          }
                          if (errors.categoryId) {
                            setErrors(prev => ({ ...prev, categoryId: "" }));
                          }
                        }}
                        onCreateOption={handleCreateCategory}
                        onEditOption={handleEditCategory}
                        onDeleteOption={handleDeleteCategory}
                        placeholder="Search or type to add category..."
                        isLoading={addingCategory}
                        error={!!errors.categoryId}
                        menuHeader="Select Category"
                        addNewText="+ Add New Category"
                        bypassInternalConfirm={true}
                      />
                      <InputErrorMsg when={errors.categoryId}>{errors.categoryId}</InputErrorMsg>
                    </div>



                    {/* Cost Field */}
                    <div className="space-y-1.5 md:col-span-1">
                      <label className="block text-[11px] font-semibold  text-slate-500 dark:text-dark-300">
                        Cost
                      </label>
                      <Input
                        type="text"
                        value={formValues.cost}
                        onChange={(e) => {
                          setFormValues((prev) => ({ ...prev, cost: e.target.value }));
                          if (errors.cost) {
                            setErrors((prev) => ({ ...prev, cost: "" }));
                          }
                        }}
                        placeholder="e.g. A$25k mentioned"
                        className="w-full"
                        error={errors.cost}
                      />
                    </div>

                    {/* Assign Field */}
                    <div className="space-y-1.5 md:col-span-1">
                      <label className="block text-[11px] font-semibold text-slate-500 dark:text-dark-300">
                        Assign
                      </label>
                      <CustomCreatableDropdown
                        options={users.map(u => ({ value: (u.userId || u.UserId)?.toString(), label: u.fullName || u.FullName, designation: u.designationName || u.DesignationName || u.roleName || u.RoleName, designations: u.designations || u.Designations || [] }))}
                        value={users.map(u => ({ value: (u.userId || u.UserId)?.toString(), label: u.fullName || u.FullName })).find(opt => opt.value === formValues.assignedToUserId?.toString()) || null}
                        formatOptionLabel={(option) => {
                          const designationsList = Array.isArray(option.designations) && option.designations.length > 0
                            ? option.designations.map(d => d.DesignationName || d.designationName).filter(Boolean)
                            : [];
                          const firstBadge = designationsList.length > 0 ? designationsList[0] : null;
                          const extraDesignations = designationsList.slice(1);

                          return (
                            <div className="flex w-full items-center justify-between min-w-0">
                              <span className="truncate pr-2">{option.label}</span>
                              {firstBadge ? (
                                <div className="ml-2 flex shrink-0 items-center justify-end gap-1">
                                  <span
                                    title={firstBadge}
                                    className="dark:bg-dark-700 shrink-0 max-w-full inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] whitespace-nowrap text-gray-500 dark:text-gray-400"
                                  >
                                    <span className="truncate">{firstBadge}</span>
                                  </span>
                                  {extraDesignations.length > 0 && (
                                    <div className="relative inline-flex items-center">
                                      <span 
                                        data-tooltip-id="global-badge-tooltip"
                                        data-tooltip-html={extraDesignations.join("<br/>")}
                                        className="dark:bg-dark-700 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap text-gray-500 dark:text-gray-400 cursor-pointer"
                                      >
                                        +{extraDesignations.length}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ) : option.designation && (
                                <span
                                  title={option.designation}
                                  className="dark:bg-dark-700 ml-2 shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] whitespace-nowrap text-gray-500 dark:text-gray-400"
                                >
                                  {option.designation}
                                </span>
                              )}
                            </div>
                          );
                        }}
                        filterOption={(candidate, input) => {
                          if (!input) return true;
                          const inputLower = input.toLowerCase();
                          const label = candidate.data.label?.toLowerCase() || "";
                          const designations = Array.isArray(candidate.data.designations) && candidate.data.designations.length > 0
                            ? candidate.data.designations.map(d => (d.DesignationName || d.designationName || "").toLowerCase())
                            : [(candidate.data.designation || "").toLowerCase()];
                          return label.includes(inputLower) || designations.some(d => d.includes(inputLower));
                        }}
                        onChange={(newValue) => {
                          if (newValue) {
                            setFormValues(prev => ({ ...prev, assignedToUserId: newValue.value }));
                          } else {
                            setFormValues(prev => ({ ...prev, assignedToUserId: "" }));
                          }
                          if (errors.assignedToUserId) {
                            setErrors(prev => ({ ...prev, assignedToUserId: "" }));
                          }
                        }}
                        isDisabled={submitting}
                        placeholder="Select User..."
                        error={!!errors.assignedToUserId}
                        menuHeader="Select User"
                      />
                      <InputErrorMsg when={errors.assignedToUserId}>{errors.assignedToUserId}</InputErrorMsg>
                    </div>

                    {/* Status Field */}
                    <div className="space-y-1.5 md:col-span-1">
                      <label className="block text-[11px] font-semibold text-slate-500 dark:text-dark-300">
                        Status
                      </label>
                      <CustomCreatableDropdown
                        options={workStatusOptions}
                        value={workStatusOptions.find(opt => opt.value === formValues.workStatusId?.toString()) || null}
                        onChange={(newValue) => {
                          if (newValue) {
                            setFormValues(prev => ({ ...prev, workStatusId: newValue.value }));
                          } else {
                            setFormValues(prev => ({ ...prev, workStatusId: "" }));
                          }
                          if (errors.workStatusId) {
                            setErrors(prev => ({ ...prev, workStatusId: "" }));
                          }
                        }}
                        onCreateOption={handleCreateWorkStatus}
                        onEditOption={handleEditWorkStatus}
                        onDeleteOption={handleDeleteWorkStatus}
                        isOptionActionable={isStatusOptionActionable}
                        placeholder="Search or select status..."
                        isLoading={addingWorkStatus}
                        error={!!errors.workStatusId}
                        menuHeader="Select Status"
                        addNewText="+ Add New Status"
                        bypassInternalConfirm={true}
                      />
                      <InputErrorMsg when={errors.workStatusId}>{errors.workStatusId}</InputErrorMsg>
                    </div>

                    {/* Cost Status Field */}
                    <div className="space-y-1.5 md:col-span-1">
                      <label className="block text-[11px] font-semibold text-slate-500 dark:text-dark-300">
                        Cost Status
                      </label>
                      <CustomCreatableDropdown
                        options={costStatusOptions}
                        value={costStatusOptions.find(opt => opt.value === formValues.costStatusId?.toString()) || null}
                        onChange={(newValue) => {
                          if (newValue) {
                            setFormValues(prev => ({ ...prev, costStatusId: newValue.value }));
                          } else {
                            setFormValues(prev => ({ ...prev, costStatusId: "" }));
                          }
                          if (errors.costStatusId) {
                            setErrors(prev => ({ ...prev, costStatusId: "" }));
                          }
                        }}
                        onCreateOption={handleCreateCostStatus}
                        onEditOption={handleEditCostStatus}
                        onDeleteOption={handleDeleteCostStatus}
                        isOptionActionable={isStatusOptionActionable}
                        placeholder="Search or select cost status..."
                        isLoading={addingCostStatus}
                        error={!!errors.costStatusId}
                        menuHeader="Select Cost Status"
                        addNewText="+ Add New Cost Status"
                        bypassInternalConfirm={true}
                      />
                      <InputErrorMsg when={errors.costStatusId}>{errors.costStatusId}</InputErrorMsg>
                    </div>

                    {/* Cost Status Assign Field */}
                    <div className="space-y-1.5 md:col-span-1">
                      <label className="block text-[11px] font-semibold text-slate-500 dark:text-dark-300">
                        Cost Status Assign
                      </label>
                      <CustomCreatableDropdown
                        options={costAssignUsers.map(u => ({ value: (u.userId || u.UserId)?.toString(), label: u.fullName || u.FullName, designation: u.Designation || u.designation || u.designationName || u.DesignationName || u.roleName || u.RoleName, designations: u.designations || u.Designations || [] }))}
                        value={costAssignUsers.map(u => ({ value: (u.userId || u.UserId)?.toString(), label: u.fullName || u.FullName})).find(opt => opt.value === formValues.costAssignedToUserId?.toString()) || null}
                        formatOptionLabel={(option) => {
                          const extraDesignations = Array.isArray(option.designations) && option.designations.length > 1
                            ? option.designations.slice(1).map(d => d.DesignationName || d.designationName).filter(Boolean)
                            : [];
                          const extraDesignationsText = extraDesignations.join(", ");

                          return (
                            <div className="flex w-full items-center justify-between">
                              <span className="truncate">{option.label}</span>
                              {Array.isArray(option.designations) && option.designations.length > 0 ? (
                                <div className="ml-2 flex shrink-0 items-center justify-end gap-1">
                                  <span
                                    key={option.designations[0].DesignationId || option.designations[0].designationId || 0}
                                    title={option.designations[0].DesignationName || option.designations[0].designationName}
                                    className="dark:bg-dark-700 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] whitespace-nowrap text-gray-500 dark:text-gray-400"
                                  >
                                    {option.designations[0].DesignationName || option.designations[0].designationName}
                                  </span>
                                  {extraDesignations.length > 0 && (
                                    <div className="relative inline-flex items-center">
                                      <span 
                                        data-tooltip-id="global-badge-tooltip"
                                        data-tooltip-html={extraDesignations.join("<br/>")}
                                        className="dark:bg-dark-700 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap text-gray-500 dark:text-gray-400 cursor-pointer"
                                      >
                                        +{extraDesignations.length}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ) : option.designation && (
                                <span
                                  title={option.designation}
                                  className="dark:bg-dark-700 ml-2 shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] whitespace-nowrap text-gray-500 dark:text-gray-400"
                                >
                                  {option.designation}
                                </span>
                              )}
                            </div>
                          );
                        }}
                        filterOption={(candidate, input) => {
                          if (!input) return true;
                          const inputLower = input.toLowerCase();
                          const label = candidate.data.label?.toLowerCase() || "";
                          const designations = Array.isArray(candidate.data.designations) && candidate.data.designations.length > 0
                            ? candidate.data.designations.map(d => (d.DesignationName || d.designationName || "").toLowerCase())
                            : [(candidate.data.designation || "").toLowerCase()];
                          return label.includes(inputLower) || designations.some(d => d.includes(inputLower));
                        }}
                        onChange={(newValue) => {
                          if (newValue) {
                            setFormValues(prev => ({ ...prev, costAssignedToUserId: newValue.value }));
                          } else {
                            setFormValues(prev => ({ ...prev, costAssignedToUserId: "" }));
                          }
                          if (errors.costAssignedToUserId) {
                            setErrors(prev => ({ ...prev, costAssignedToUserId: "" }));
                          }
                        }}
                        isDisabled={submitting}
                        placeholder="Select User..."
                        error={!!errors.costAssignedToUserId}
                        menuHeader="Select User"
                      />
                      <InputErrorMsg when={errors.costAssignedToUserId}>{errors.costAssignedToUserId}</InputErrorMsg>
                    </div>
                                        {/* Client Wish Rich Text Field (Spans 2 columns) */}
                    <div className="space-y-1.5 md:col-span-2">
                      <label className="block text-[11px] font-semibold text-slate-500 dark:text-dark-300">
                        Client Wish
                      </label>
                      <RichTextEditor
                        value={formValues.wish || ""}
                        onChange={(val) => {
                          setFormValues((prev) => ({ ...prev, wish: val }));
                          if (errors.wish) {
                            setErrors((prev) => ({ ...prev, wish: "" }));
                          }
                        }}
                        placeholder="Enter Client Wish..."
                        height="220px"
                        error={!!errors.wish}
                      />
                      <InputErrorMsg when={errors.wish}>{errors.wish}</InputErrorMsg>
                    </div>

                    {/* Dynamic Fields Section (Spans 2 columns) */}
                    <div className="md:col-span-2 border-t border-gray-100 dark:border-dark-750 pt-4 mt-2">
                      <EntityDynamicFieldsForm
                        ref={dynamicFieldsRef}
                        entityTypeCode={entityTypeCode || EntityTypeEnum.LeadClientNote}
                        entityId={show && formValues.id ? formValues.id : null}
                        onFieldAdded={onFieldAdded}
                        autoAddAttachments={true}
                      />
                    </div>
                  </div>
                </div>

                {/* Footer - Gray Background */}
                <div className="bg-gray-50 dark:bg-gray-800 px-8 py-5 flex items-center justify-end gap-3 border-t border-gray-150 dark:border-gray-800 flex-shrink-0">
                  <Button
                    type="button"
                    onClick={onClose}
                    variant="outlined"
                    color="neutral"
                    disabled={submitting}
                    className="px-5 py-2 text-xs font-semibold rounded-lg"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={submitting}
                    className="min-w-[100px] px-5 py-2 text-xs font-semibold rounded-lg"
                    onClick={onSubmit}
                  >
                    {submitting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Saving...
                      </div>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
    {show && typeof document !== 'undefined' && createPortal(
      <ReactTooltip 
        id="global-badge-tooltip"
        place="right"
        positionStrategy="fixed"
        className="!z-[99999] !bg-gray-900/90 dark:!bg-gray-700/90 !backdrop-blur-sm !text-white !text-[10px] !px-2 !py-1 !rounded-md !shadow-lg"
      />,
      document.body
    )}
    </>
  );
}
